import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from 'components';
import { usePaginatedResults } from 'hooks/usePaginatedResults';
import { trackError } from 'utils/trackError';
import { ConsultationCard } from './ConsultationCard';
import { Empty, Heading, List, Section, Sentinel } from './PastConsultations.styles';

interface PastConsultationsProps {
  medatixxPatientId: string | undefined;
}

export const PastConsultations = ({ medatixxPatientId }: PastConsultationsProps) => {
  const { t } = useTranslation();
  const {
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    resultsState,
    handleStatusUpdate,
    sentinelRef,
    fetchNextPage,
  } = usePaginatedResults({ medatixxPatientId }, { enabled: !!medatixxPatientId });

  useEffect(() => {
    if (error) trackError(error, { context: 'patientOverviewPastConsultations' });
  }, [error]);

  if (isLoading) {
    return (
      <Section data-ph-no-autocapture>
        <Heading>{t('patientOverview.pastConsultations.title')}</Heading>
        <div role="status" aria-label={t('patientOverview.pastConsultations.title')}>
          <Spinner size="xs" />
        </div>
      </Section>
    );
  }

  return (
    <Section data-ph-no-autocapture>
      <Heading>{t('patientOverview.pastConsultations.title')}</Heading>
      {resultsState.length > 0 ? (
        <List role="list" aria-label={t('patientOverview.pastConsultations.title')}>
          {resultsState.map((result) => (
            <ConsultationCard key={result.id} result={result} onStatusUpdate={handleStatusUpdate} />
          ))}
        </List>
      ) : isError ? (
        <Empty role="alert">{t('patientOverview.pastConsultations.loadError')}</Empty>
      ) : (
        <Empty>{t('patientOverview.pastConsultations.empty')}</Empty>
      )}
      {isError && resultsState.length > 0 && (
        <Empty role="alert">
          {t('patientOverview.pastConsultations.paginationError')}{' '}
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => void fetchNextPage()}
          >
            {t('general.retry')}
          </Button>
        </Empty>
      )}
      {hasNextPage && (
        <Sentinel
          ref={sentinelRef}
          aria-hidden="true"
          data-pagination-sentinel="past-consultations"
        />
      )}
      {isFetchingNextPage && (
        <div role="status" aria-label={t('general.loading')}>
          <Spinner size="xs" />
        </div>
      )}
    </Section>
  );
};
